// B-04 — Fiche contact détail.
// Onglets : Infos, Dossiers liés (livrés en PR1).
// Documents : fichiers partagés avec le client. Activité → à venir.
import { usePublishPageCrumb } from '../../store/usePageSettingsStore';
import { useState } from 'react';
import { Link, useNavigate, useParams, NavLink, Routes, Route, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, Mail, MapPin, Pencil, Phone, Plus, Trash2,
} from 'lucide-react';
import {
  FaBriefcase, FaBuilding, FaCircleInfo, FaClockRotateLeft, FaFileLines, FaUser,
} from '../ui/FaIcons';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import { useContact, useContactDossiers, useDeleteContact } from '../../hooks/queries/useContacts';
import { useBillingDocuments } from '../../hooks/queries/useBillingDocuments';
import {
  CONTACT_CATEGORY_LABELS,
  CONTACT_KIND_LABELS,
  DOSSIER_CONTACT_ROLE_LABELS,
  type Contact,
  getContactDisplayName,
} from '../../services/contacts/contactService';
import {
  computeFinancialSummary,
  formatEuros,
} from '../../services/billing/billingService';
import { ContactEditModal } from './ContactEditModal';
import { DossierCreateModal } from '../dossiers/DossierCreateModal';
import { InviteClientButton } from '../portal/InviteClientButton';
import { StageBadge, TypeBadge } from '../dossiers/dossierBadges';

import { formatAppDate } from '../../lib/dateFormat';
import { useSharedFilesForDossiers } from '../../hooks/queries/useDossierFiles';
import { formatFileSize } from '../../services/files/fileUtils';
import type { DossierFile } from '../../services/files/filesService';
import { FileKindBadge } from '../files/FileRow';
import { FilePreviewModal } from '../files/FilePreviewModal';
const TABS = [
  { key: 'infos',     label: 'Infos',         to: 'infos',     icon: FaCircleInfo },
  { key: 'dossiers',  label: 'Dossiers liés', to: 'dossiers',  icon: FaBriefcase },
  { key: 'documents', label: 'Documents',     to: 'documents', icon: FaFileLines },
  { key: 'activity',  label: 'Activité',      to: 'activity',  icon: FaClockRotateLeft },
];

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const canEdit = canAccessFeature(asCollaboratorRole(collaborator?.role), 'contacts_edit');
  const canDelete = canAccessFeature(asCollaboratorRole(collaborator?.role), 'contacts_delete');

  const { data: contact, isLoading, error } = useContact(id);
  const { data: dossierLinks } = useContactDossiers(id);
  usePublishPageCrumb(contact ? getContactDisplayName(contact) : null);
  const deleteMut = useDeleteContact();

  const [editOpen, setEditOpen] = useState(false);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Spinner /></div>;
  }
  if (error || !contact) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <p className="text-sm text-red-600">
          {error instanceof Error ? error.message : 'Contact introuvable.'}
        </p>
        <Button variant="ghost" onClick={() => navigate('/contacts')}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour au répertoire
        </Button>
      </div>
    );
  }

  return (
    <div className="main-container-gdm flex-1 flex flex-col min-h-0 gap-5">
      <div>
        <Link
          to="/contacts"
          className="inline-flex items-center gap-1.5 text-sm text-cime-text-muted hover:text-cime-primary mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour au répertoire
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full bg-[rgba(4,52,41,0.07)] text-cime-primary">
              {contact.kind === 'corporate' ? <FaBuilding className="h-5 w-5" aria-hidden /> : <FaUser className="h-5 w-5" aria-hidden />}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-neutral-400">
                {CONTACT_KIND_LABELS[contact.kind]}
              </p>
              <div className="flex flex-wrap items-center gap-2.5">
                <h1
                  className="truncate text-[28px] font-semibold leading-tight text-cime-text-primary"
                  style={{ fontFamily: 'var(--cime-font-heading)' }}
                >
                  {getContactDisplayName(contact)}
                </h1>
                <span className="inline-flex items-center rounded-full bg-[rgba(47,122,85,0.1)] px-[9px] py-0.5 text-xs font-semibold text-[#2f7a55]">
                  {CONTACT_CATEGORY_LABELS[contact.contact_category]}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <InviteClientButton contactId={contact.id} />
            {canDelete && (
              <Button
                variant="danger"
                onClick={() => {
                  if (window.confirm(`Supprimer définitivement « ${getContactDisplayName(contact)} » ?`)) {
                    deleteMut.mutate(contact.id, {
                      onSuccess: () => navigate('/contacts'),
                    });
                  }
                }}
                disabled={deleteMut.isPending}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Supprimer
              </Button>
            )}
            {canEdit && (
              <Button variant="primary" onClick={() => setEditOpen(true)}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifier
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Onglets */}
      <nav aria-label="Onglets du contact" className="flex items-center border-b border-[#e6e2d8]">
        {TABS.map(({ key, label, to, icon: Icon }) => {
          const count = key === 'dossiers' ? dossierLinks?.length : undefined;
          return (
            <NavLink
              key={key}
              to={`/contacts/${contact.id}/${to}`}
              end
              className={({ isActive }) =>
                cn(
                  'group -mb-px inline-flex items-center whitespace-nowrap border-b-2 px-3 pb-2.5 pt-[11px] text-sm transition-colors',
                  isActive
                    ? 'is-active border-cime-primary font-semibold text-cime-primary'
                    : 'border-transparent font-medium text-cime-text-muted hover:text-cime-text-primary',
                )
              }
            >
              <Icon className="mr-[7px] h-[13px] w-[13px] opacity-70 group-[.is-active]:opacity-100" aria-hidden />
              {label}
              {count ? (
                <span className="ml-1.5 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none text-neutral-400 group-[.is-active]:bg-[#e8f0ec] group-[.is-active]:text-cime-primary">
                  {count}
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </nav>

      <div className="flex-1 min-h-0 flex items-stretch">
          <Card
            className="flex-1 min-w-0 min-h-0 flex flex-col"
            contentClassName="flex-1 min-h-0 flex flex-col overflow-y-auto"
          >
            <Routes>
              <Route index element={<Navigate to="infos" replace />} />
              <Route path="infos" element={<InfosTab contact={contact} />} />
              <Route path="dossiers" element={<DossiersTab contact={contact} />} />
              <Route path="documents" element={<DocumentsTab contact={contact} />} />
              <Route
                path="activity"
                element={
                  <TabPlaceholder
                    icon={FaClockRotateLeft}
                    title="Activité"
                    text="Cet onglet sera alimenté par l'historique unifié des actions du cabinet."
                  />
                }
              />
            </Routes>
          </Card>
      </div>

      {editOpen && (
        <ContactEditModal contact={contact} onClose={() => setEditOpen(false)} />
      )}
    </div>
  );
}

// ─── Onglet Infos ────────────────────────────────────────────────────────────
function InfosTab({ contact }: { contact: Contact }) {
  return (
    <div className="grid w-full grid-cols-1 items-start gap-4 p-6 xl:grid-cols-[minmax(0,1fr)_330px]">
      <div className="min-w-0 space-y-4">
        <ActiveDossiersCard contact={contact} />
        <FinancialSummaryCard contact={contact} />
        <Section title="Notes internes">
          {contact.notes ? (
            <p className="text-sm leading-relaxed text-cime-text-primary whitespace-pre-wrap">{contact.notes}</p>
          ) : (
            <p className="text-sm italic text-cime-text-muted">Aucune note interne.</p>
          )}
        </Section>
      </div>
      <div className="space-y-4">
          <Section title="Coordonnées">
            <dl className="grid grid-cols-1 gap-y-2.5 text-sm">
              {contact.kind === 'corporate' && contact.siret && (
                <Field label="SIRET" value={<span className="tabular-nums">{contact.siret}</span>} />
              )}
              <Field label="Email" value={contact.email ? (
                <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1 text-cime-primary hover:underline">
                  <Mail className="w-3.5 h-3.5" /> {contact.email}
                </a>
              ) : '—'} />
              <Field label="Téléphone" value={contact.phone ? (
                <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1 text-cime-primary hover:underline">
                  <Phone className="w-3.5 h-3.5" /> {contact.phone}
                </a>
              ) : '—'} />
            </dl>
          </Section>

          <Section title="Adresse">
            {contact.address_line1 || contact.city ? (
              <address className="text-sm not-italic text-cime-text-primary leading-relaxed">
                <MapPin className="inline w-3.5 h-3.5 text-cime-text-muted mr-1.5" />
                {contact.address_line1 && <>{contact.address_line1}<br /></>}
                {contact.address_line2 && <>{contact.address_line2}<br /></>}
                {(contact.postal_code || contact.city) && (
                  <>{contact.postal_code} {contact.city}<br /></>
                )}
                {contact.country && <span className="text-cime-text-muted">{contact.country}</span>}
              </address>
            ) : (
              <p className="text-sm italic text-cime-text-muted">Aucune adresse renseignée.</p>
            )}
          </Section>
      </div>
    </div>
  );
}

// ─── Onglet Dossiers liés ────────────────────────────────────────────────────
function DossiersTab({ contact }: { contact: Contact }) {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const { data: links, isLoading } = useContactDossiers(contact.id);
  const collaborator = useAuthStore((s) => s.collaborator);
  const canCreateDossier = canAccessFeature(asCollaboratorRole(collaborator?.role), 'dossiers_create');

  return (
    <div className="w-full mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-cime-text-primary">
          Dossiers liés <span className="text-sm text-cime-text-muted font-normal">({links?.length ?? 0})</span>
        </h2>
        {canCreateDossier && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Nouveau dossier
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8"><Spinner /></div>
      ) : !links || links.length === 0 ? (
        <Section>
          <p className="text-sm italic text-cime-text-muted text-center py-4">
            Ce contact n'est lié à aucun dossier.
          </p>
        </Section>
      ) : (
        <div className="space-y-2">
          {links.map((link) => (
            <button
              key={`${link.dossier_id}-${link.role}`}
              onClick={() => navigate(`/dossiers/${link.dossier_id}`)}
              className="w-full bg-white border border-neutral-200 rounded-cime p-3 text-left hover:border-cime-primary/40 hover:bg-neutral-50 transition"
            >
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Briefcase className="w-3.5 h-3.5 text-cime-primary" />
                <span className="text-xs text-cime-text-muted">
                  {link.dossier.number}
                </span>
                <TypeBadge type={link.dossier.type_affaire} />
                <StageBadge stage={link.dossier.stage} />
                <span className="ml-auto text-[10px] uppercase tracking-wider text-neutral-400">
                  {DOSSIER_CONTACT_ROLE_LABELS[link.role]}
                  {link.is_primary && <span className="ml-1 text-cime-primary">★</span>}
                </span>
              </div>
              <p className="text-sm font-medium text-cime-text-primary">
                {link.dossier.title}
              </p>
              <p className="text-[11px] text-cime-text-muted mt-0.5">
                Lié le {formatAppDate(link.added_at)}
              </p>
            </button>
          ))}
        </div>
      )}

      {createOpen && (
        <DossierCreateModal
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Onglet Documents : fichiers partagés avec le contact ──────────────────────
function DocumentsTab({ contact }: { contact: Contact }) {
  const { data: links, isLoading: linksLoading } = useContactDossiers(contact.id);
  const dossierIds = [...new Set((links ?? []).map((l) => l.dossier_id))];
  const { data: files, isLoading: filesLoading } = useSharedFilesForDossiers(dossierIds);
  const [preview, setPreview] = useState<DossierFile | null>(null);
  const loading = linksLoading || (dossierIds.length > 0 && filesLoading);

  return (
    <div className="w-full p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-cime-text-primary">
          Documents partagés{' '}
          <span className="text-sm text-cime-text-muted font-normal">({files?.length ?? 0})</span>
        </h2>
        <p className="text-[13px] text-cime-text-muted">
          Les fichiers de ses dossiers que le cabinet a partagés avec ce contact.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8"><Spinner /></div>
      ) : !files || files.length === 0 ? (
        <TabPlaceholder
          icon={FaFileLines}
          title="Aucun document partagé"
          text="Pour partager un fichier, ouvrez-le dans l'onglet Fichiers du dossier et rendez-le visible du client."
        />
      ) : (
        <ul className="overflow-hidden rounded-[10px] border border-neutral-200 bg-white">
          {files.map((file) => (
            <li key={file.id} className="border-b border-neutral-100 last:border-b-0">
              <button
                type="button"
                onClick={() => setPreview(file)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-[#fbfaf7]"
              >
                <FileKindBadge filename={file.filename} mime={file.mime} />
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-sm font-medium text-cime-text-primary">
                    {file.filename}
                  </span>
                  <span className="block truncate text-xs text-neutral-400">
                    {file.dossier ? `${file.dossier.number} — ${file.dossier.title}` : ''}
                  </span>
                </span>
                <span className="hidden shrink-0 text-xs tabular-nums text-neutral-400 sm:block">
                  {formatFileSize(file.size)}
                </span>
                <span className="w-[110px] shrink-0 text-right text-xs tabular-nums text-neutral-400">
                  {formatAppDate(file.created_at)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

// ─── Dossiers actifs (aperçu sur la fiche) ───────────────────────────────────
function ActiveDossiersCard({ contact }: { contact: Contact }) {
  const navigate = useNavigate();
  const { data: links, isLoading } = useContactDossiers(contact.id);

  const active = (links ?? []).filter(
    (l) => !l.dossier.closed_at && !l.dossier.archived_at,
  );

  if (isLoading) {
    return (
      <Section title="Dossiers actifs">
        <div className="flex items-center justify-center py-4"><Spinner /></div>
      </Section>
    );
  }
  if (active.length === 0) return null;

  return (
    <Section title={`Dossiers actifs (${active.length})`}>
      <div className="-m-4">
        {active.map((link) => (
          <button
            key={`${link.dossier_id}-${link.role}`}
            onClick={() => navigate(`/dossiers/${link.dossier_id}`)}
            className="flex w-full items-center gap-3 border-b border-neutral-100 px-4 py-2.5 text-left transition last:border-b-0 hover:bg-[#fbfaf7]"
          >
            <Briefcase className="h-3.5 w-3.5 shrink-0 text-cime-primary" />
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block text-[11px] tracking-[0.04em] text-neutral-400">{link.dossier.number}</span>
              <span className="block truncate text-sm text-cime-text-primary">{link.dossier.title}</span>
            </span>
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
              {DOSSIER_CONTACT_ROLE_LABELS[link.role]}
            </span>
            <StageBadge stage={link.dossier.stage} />
          </button>
        ))}
      </div>
    </Section>
  );
}

// ─── Résumé financier du contact (en tant que client) ────────────────────────
function FinancialSummaryCard({ contact }: { contact: Contact }) {
  const { data: docs, isLoading } = useBillingDocuments({ client_contact_id: contact.id });

  if (isLoading) {
    return (
      <Section title="Résumé financier">
        <div className="flex items-center justify-center py-4"><Spinner /></div>
      </Section>
    );
  }

  const summary = computeFinancialSummary(docs ?? []);

  return (
    <Section title="Résumé financier">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Facturé TTC" value={formatEuros(summary.invoicedTtc)} />
        <Metric label="Encaissé" value={formatEuros(summary.paidTtc)} tone="positive" />
        <Metric
          label="Reste dû"
          value={formatEuros(summary.outstandingTtc)}
          tone={summary.outstandingTtc > 0 ? 'warning' : 'muted'}
        />
        <Metric
          label="En retard"
          value={formatEuros(summary.overdueTtc)}
          tone={summary.overdueTtc > 0 ? 'danger' : 'muted'}
        />
      </div>
      <p className="text-[11px] text-cime-text-muted mt-3">
        {summary.invoiceCount === 0
          ? 'Aucune facture émise pour ce contact'
          : `${summary.invoiceCount} facture${summary.invoiceCount > 1 ? 's' : ''} émise${summary.invoiceCount > 1 ? 's' : ''}`}
        {summary.creditNoteTtc > 0 && ` · ${formatEuros(summary.creditNoteTtc)} d'avoirs`}
      </p>
    </Section>
  );
}

function Metric({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'positive' | 'warning' | 'danger' | 'muted';
}) {
  const toneClass = {
    default: 'text-cime-text-primary',
    positive: 'text-[#2f7a55]',
    warning: 'text-[#8a6d1f]',
    danger: 'text-[#c05b33]',
    muted: 'text-neutral-400',
  }[tone];
  const cardClass = {
    default: 'border-neutral-200 bg-white',
    positive: 'border-neutral-200 bg-white',
    warning: 'border-[rgba(201,168,76,0.45)] bg-gradient-to-b from-white to-[#fdf9ee]',
    danger: 'border-[rgba(192,91,51,0.35)] bg-gradient-to-b from-white to-[#fdf6f2]',
    muted: 'border-neutral-200 bg-white',
  }[tone];
  return (
    <div className={cn('rounded-lg border px-3.5 py-3', cardClass)}>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">{label}</p>
      <p
        className={cn('text-[22px] font-semibold leading-tight tabular-nums', toneClass)}
        style={{ fontFamily: 'var(--cime-font-heading)' }}
      >
        {value}
      </p>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-[10px] border border-neutral-200 bg-white">
      {title && (
        <header className="border-b border-[#e6e2d8] bg-[#fbfaf7] px-4 py-2.5">
          <h3 className="text-[13px] font-semibold text-cime-primary">{title}</h3>
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="mb-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">{label}</dt>
      <dd className="text-sm text-cime-text-primary">{value}</dd>
    </div>
  );
}

function TabPlaceholder({
  icon: Icon,
  title,
  text,
}: {
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  title: string;
  text: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-14 text-center">
      <span className="grid h-[52px] w-[52px] place-items-center rounded-full border border-[#e6e2d8] bg-[#fbfaf7] text-neutral-400">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <p className="text-sm font-semibold text-cime-text-primary">{title}</p>
      <p className="max-w-[420px] text-[13px] leading-relaxed text-cime-text-muted">{text}</p>
      <span className="mt-0.5 inline-flex items-center rounded-full border border-neutral-300 px-2.5 py-0.5 text-[11px] font-semibold text-cime-text-muted">
        À venir
      </span>
    </div>
  );
}
